/**
 * reviewChanges.ts
 *
 * Pure diff/compute logic behind the #969 Part F Review Changes panel — the
 * editor's post-refresh review workflow. Everything here is a pure function
 * of a DashboardConfig plus data the caller has already fetched; the
 * network/fetch side lives in hooks/useReviewChanges.ts so this stays
 * unit-testable without a server.
 *
 * Grouped by type, per the issue:
 *   - new_columns / new_rows   — diff a table widget's live labels against
 *                                the snapshot captured at the last sign-off
 *                                (TableGraphWidgetData.sign_off_snapshot).
 *   - missing_table            — a table widget whose table_path 404s
 *                                (#969 Part E).
 *   - ai_regenerated           — a multi_table_analysis card whose signed
 *                                text has been superseded (#963's
 *                                needs_review, computed server-side by
 *                                dashboardcards.ComputeReviewState). NOTE:
 *                                that flag also covers "the data wave moved
 *                                but nobody regenerated" — this package does
 *                                not currently distinguish the two, so a
 *                                card can appear here on a wave-only lapse.
 *                                Documented simplification, not a bug.
 *   - orphaned_cleaned         — a hide-list entry silently dropped because
 *                                its column/row no longer exists (#969 Part
 *                                D). This package does not itself detect
 *                                orphaned references — that lives with the
 *                                Tier 1 view-config/blocklist logic (#969
 *                                Part B/D, out of scope here) — so
 *                                `computeReviewChanges` only renders
 *                                whatever the caller passes via
 *                                `orphanedCleared`; today that is always
 *                                empty until Part B/D lands.
 *
 * There is deliberately no baseline-less diffing: with no sign-off (or no
 * snapshot for a given widget) there is nothing to compare against, so
 * nothing is reported for it — same "unknown means nothing to compare"
 * convention as goapi/pkg/dashboardcards.Resolver.
 */

import { DashboardConfig, TableGraphWidgetData, MultiTableAnalysisWidgetData } from '../types/gotypes'
import { walkTableGraphWidgets, walkMultiTableWidgets } from './dashboardWidgetWalk'

export type ReviewChangeType = 'new_columns' | 'new_rows' | 'ai_regenerated' | 'orphaned_cleaned' | 'missing_table'

export interface ReviewChangeItem {
  id: string
  type: ReviewChangeType
  /** Path from dashboardWidgetWalk — the widget this item concerns. */
  widgetPath: string
  pageIndex: number
  /** Display name for the widget ("names its widget", per the issue). */
  widgetTitle: string
  message: string
  /** The new/cleared labels, where applicable (for detail display). */
  labels?: string[]
}

/** Live state of one table_graph widget's source, as loaded for the panel. */
export interface LiveTableInfo {
  missing?: boolean
  columns?: string[]
  rows?: string[]
  /** Dead view-config references found while the live data was in hand (#969 Part B×F). */
  orphans?: string[]
}

/** An orphaned hide-list entry already cleaned elsewhere (#969 Part B/D) — see module doc. */
export interface OrphanedReference {
  widgetPath: string
  pageIndex: number
  widgetTitle: string
  label: string
  kind: 'column' | 'row'
}

function tableWidgetTitle(w: TableGraphWidgetData): string {
  if (w.title) return w.title
  if (w.table_path) return w.table_path.split('/').pop()?.replace(/\.[^.]+$/, '') || w.table_path
  return '(untitled table)'
}

function analysisWidgetTitle(w: MultiTableAnalysisWidgetData): string {
  return w.title || w.multi_table_id || '(untitled analysis)'
}

/** Labels present in `after` that were not in `before`, in `after`'s order.
 *  `before` undefined ("no baseline") always yields no diff — see module doc. */
export function newLabels(before: string[] | undefined, after: string[] | undefined): string[] {
  if (!before || !after) return []
  const seen = new Set(before)
  return after.filter(label => !seen.has(label))
}

export interface ComputeReviewChangesInput {
  config: DashboardConfig
  /** Keyed by widget path (dashboardWidgetWalk). Entries not present are
   *  treated as "not loaded yet" — no item is emitted for that widget. */
  liveTables: Record<string, LiveTableInfo>
  orphanedCleared?: OrphanedReference[]
}

/** Builds the full, grouped-by-type change list for the Review Changes panel. */
export function computeReviewChanges({ config, liveTables, orphanedCleared }: ComputeReviewChangesInput): ReviewChangeItem[] {
  const items: ReviewChangeItem[] = []

  // With no sign-off there is no "since last sign-off" baseline at all — the
  // panel has nothing to show (DashboardView only offers it once needs_review
  // is true, which itself requires a sign-off to have lapsed).
  if (!config.sign_off) return items

  for (const ref of walkTableGraphWidgets(config)) {
    const live = liveTables[ref.path]
    if (!live) continue
    const title = tableWidgetTitle(ref.data)

    if (live.missing) {
      items.push({
        id: `missing:${ref.path}`,
        type: 'missing_table',
        widgetPath: ref.path,
        pageIndex: ref.pageIndex,
        widgetTitle: title,
        message: `Source table '${ref.data.table_path}' is missing — widget requires attention.`,
      })
      continue
    }

    const snapshot = ref.data.sign_off_snapshot
    const addedColumns = newLabels(snapshot?.columns, live.columns)
    const addedRows = newLabels(snapshot?.rows, live.rows)

    if (addedColumns.length > 0) {
      items.push({
        id: `new_columns:${ref.path}`,
        type: 'new_columns',
        widgetPath: ref.path,
        pageIndex: ref.pageIndex,
        widgetTitle: title,
        message: `${addedColumns.join(', ')} added to ${title}`,
        labels: addedColumns,
      })
    }
    if (addedRows.length > 0) {
      items.push({
        id: `new_rows:${ref.path}`,
        type: 'new_rows',
        widgetPath: ref.path,
        pageIndex: ref.pageIndex,
        widgetTitle: title,
        message: `${addedRows.join(', ')} added to source table on ${title}`,
        labels: addedRows,
      })
    }
  }

  for (const ref of walkMultiTableWidgets(config)) {
    if (!ref.data.needs_review) continue
    const title = analysisWidgetTitle(ref.data)
    items.push({
      id: `ai_regenerated:${ref.path}`,
      type: 'ai_regenerated',
      widgetPath: ref.path,
      pageIndex: ref.pageIndex,
      widgetTitle: title,
      message: `Gen AI Insights regenerated on ${title}`,
    })
  }

  for (const orphan of orphanedCleared || []) {
    items.push({
      id: `orphaned:${orphan.widgetPath}:${orphan.kind}:${orphan.label}`,
      type: 'orphaned_cleaned',
      widgetPath: orphan.widgetPath,
      pageIndex: orphan.pageIndex,
      widgetTitle: orphan.widgetTitle,
      message: `'${orphan.label}' was removed from source; hide instruction cleared on ${orphan.widgetTitle}`,
    })
  }

  return items
}

export const REVIEW_CHANGE_TYPE_LABELS: Record<ReviewChangeType, string> = {
  new_columns: 'New columns added',
  new_rows: 'New rows added',
  ai_regenerated: 'AI text regenerated',
  orphaned_cleaned: 'Orphaned references cleaned',
  missing_table: 'Missing table references',
}

/** Stable display order for the panel's groups — broken references first
 *  (they block sign-off), then content changes, then AI text, then cleanup. */
export const REVIEW_CHANGE_TYPE_ORDER: ReviewChangeType[] = [
  'missing_table',
  'new_columns',
  'new_rows',
  'ai_regenerated',
  'orphaned_cleaned',
]
